import React, { useEffect, useState } from 'react';
import { Grid } from '@mui/material';
import { ChevronRight } from 'lucide-react';
import { Order } from '../../types';
import { ordersAPI } from '../../services/api';
import { PageHeader, Card, SectionTitle, StatusBadge } from '../../components/common/UIComponents';

const AdminBookings: React.FC = () => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [selected, setSelected] = useState<Order | null>(null);

  useEffect(() => { ordersAPI.getAll().then(setOrders); }, []);

  return (
    <div className="min-h-screen px-8 py-7" style={{ background: 'var(--bg-base)' }}>
      <PageHeader title="Booking Management" subtitle="Review, approve and manage all campaign bookings" />

      <Grid container spacing={3}>
        {/* Orders List */}
        <Grid item xs={12} lg={selected ? 7 : 12}>
          <div className="flex flex-col gap-2.5">
            {orders.map(o => (
              <div
                key={o.id}
                onClick={() => setSelected(o === selected ? null : o)}
                className="bg-white rounded-2xl px-5 py-4 cursor-pointer transition-all duration-200"
                style={{
                  border: `1.5px solid ${selected?.id === o.id ? 'var(--brand)' : 'var(--border)'}`,
                  boxShadow: selected?.id === o.id ? 'var(--shadow-brand)' : 'var(--shadow-sm)',
                }}
              >
                <div className="flex flex-wrap justify-between items-center gap-2.5">
                  <div className="flex-1">
                    <div className="flex flex-wrap items-center gap-2.5 mb-1.5">
                      <span className="font-bold text-sm" style={{ color: 'var(--text-primary)' }}>{o.campaignName}</span>
                      <span className="text-xs" style={{ color: 'var(--text-muted)' }}>{o.id}</span>
                    </div>
                    <div className="flex flex-wrap gap-3.5 text-xs" style={{ color: 'var(--text-muted)' }}>
                      <span>{o.companyName}</span>
                      <span>{o.items.length} location{o.items.length > 1 ? 's' : ''}</span>
                      <span className="font-semibold" style={{ color: 'var(--brand)' }}>₹{o.grandTotal.toLocaleString('en-IN')}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span
                      className="text-[11px] font-semibold px-2.5 py-[3px] rounded-full"
                      style={{
                        background: o.paymentStatus === 'Received' ? 'var(--success-bg)' : 'var(--warning-bg)',
                        color: o.paymentStatus === 'Received' ? 'var(--success)' : 'var(--warning)',
                        fontFamily: 'DM Sans',
                      }}
                    >
                      {o.paymentStatus}
                    </span>
                    <StatusBadge status={o.status} size="sm" />
                    <ChevronRight size={15} color="var(--text-muted)" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Grid>

        {/* Order Detail Panel */}
        {selected && (
          <Grid item xs={12} lg={5}>
            <Card style={{ position: 'sticky', top: 80 }}>
              <div className="flex justify-between items-center mb-4">
                <SectionTitle>Order Details</SectionTitle>
                <button
                  onClick={() => setSelected(null)}
                  className="flex items-center justify-center w-7 h-7 rounded-lg text-base cursor-pointer transition-colors hover:brightness-95"
                  style={{ background: 'var(--bg-muted)', border: '1px solid var(--border)', color: 'var(--text-muted)' }}
                >
                  ×
                </button>
              </div>

              {/* Campaign Header */}
              <div className="rounded-xl px-3.5 py-3 mb-4" style={{ background: 'var(--brand-light)', border: '1px solid var(--brand-mid)' }}>
                <div className="font-bold text-[15px] mb-0.5" style={{ color: 'var(--brand)' }}>{selected.campaignName}</div>
                <div className="text-xs" style={{ color: 'var(--text-muted)' }}>{selected.companyName} · {selected.id}</div>
              </div>

              {/* Items */}
              <div className="flex flex-col gap-2.5 mb-4">
                {selected.items.map((item, i) => (
                  <div key={i} className="flex gap-2.5 p-2.5 rounded-[10px]" style={{ background: 'var(--bg-muted)', border: '1px solid var(--border-light)' }}>
                    <img src={item.space.images[0]} alt="" className="w-[60px] h-11 object-cover rounded-lg shrink-0" />
                    <div>
                      <div className="font-semibold text-[13px] mb-0.5" style={{ color: 'var(--text-primary)' }}>{item.space.title}</div>
                      <div className="text-[11px]" style={{ color: 'var(--text-muted)' }}>{item.space.city} · ₹{item.space.rentAmount.toLocaleString('en-IN')}/mo</div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Grand Total */}
              <div className="flex justify-between items-center py-3 mb-4" style={{ borderTop: '1px solid var(--border)' }}>
                <span className="font-semibold text-sm" style={{ color: 'var(--text-primary)' }}>Grand Total</span>
                <span className="font-bold text-xl" style={{ color: 'var(--brand)', fontFamily: 'Playfair Display, serif' }}>₹{selected.grandTotal.toLocaleString('en-IN')}</span>
              </div>

              {/* Actions */}
              <div className="flex gap-2">
                <button className="flex-1 rounded-[9px] py-2.5 font-semibold text-[13px] cursor-pointer transition-all hover:brightness-95" style={{ background: 'var(--success-bg)', border: '1px solid #b8e8ce', color: 'var(--success)', fontFamily: 'DM Sans' }}>
                  ✓ Approve
                </button>
                <button className="flex-1 rounded-[9px] py-2.5 font-semibold text-[13px] cursor-pointer transition-all hover:brightness-95" style={{ background: 'var(--info-bg)', border: '1px solid #c5d8f8', color: 'var(--info)', fontFamily: 'DM Sans' }}>
                  Assign Staff
                </button>
                <button className="flex-1 rounded-[9px] py-2.5 font-semibold text-[13px] cursor-pointer transition-all hover:brightness-95" style={{ background: 'var(--danger-bg)', border: '1px solid #f8c0c0', color: 'var(--danger)', fontFamily: 'DM Sans' }}>
                  ✗ Reject
                </button>
              </div>
            </Card>
          </Grid>
        )}
      </Grid>
    </div>
  );
};

export default AdminBookings;